import { Component, OnInit, AfterViewInit, ChangeDetectorRef, EventEmitter, ElementRef, Output, ViewContainerRef } from '@angular/core';
import { FormGroup, FormControl, Validators, NgForm } from '@angular/forms';
import { Onboarding, OnbValidators, ShipMethod, BillingPeriodicity, Status, getStepNumber } from '../../../models/onboarding';
import { distinctUntilChanged, debounceTime, switchMap, take } from 'rxjs/operators';
import { Product } from '../../../models/product';
import { ApiService } from '../../../services/api.service';
import { Addresses } from '../../../models/addresses';
import { environment } from '../../../../environments/environment';
import { addIdToSelectInputElement } from '../../../utils/addIdToSelectInputElement';
import { fromEvent } from 'rxjs/observable/fromEvent';
import { AlgGtmService } from '../../../services/alg-gtm.service';
import { ModalDialogService } from 'ngx-modal-dialog';
import { UtilsService } from '../../../services/utils.service';
import { AlgConditionModalComponent } from '../../dialogs/alg-condition-modal/alg-condition-modal.component';
import { TranslationService } from 'angular-l10n';

interface GeocodeResult {
  results: [
    {
      address_components: [
        {
          short_name: string;
          long_name: string;
          types: string[];
        }
      ];
    }
  ];
}

@Component({
  selector: 'alg-contract-info',
  templateUrl: './alg-contract-info.component.html',
  styleUrls: ['./alg-contract-info.component.scss'],
})
export class AlgContractInfoComponent implements OnInit {
  public contractInfoForm: FormGroup = new FormGroup({});
  public onboarding: Onboarding = {} as Onboarding;
  public product: Product = {} as Product;
  public goAhead = false;
  public differentAddress = false;
  public enableOtherAddress = false;

  // residence event emitters
  public residenceCapUpdate = new EventEmitter<string>();
  public residenceCitiesUpdate = new EventEmitter<string>();
  public residenceProvincesUpdate = new EventEmitter<string>();
  public residenceAddressesUpdate = new EventEmitter<string>();

  // residence autocomplete lists
  public residenceCaps = [];
  public residenceCities = [];
  public residenceProvinces = [];
  public residenceAddresses = [];

  // forniture event emitters
  public fornitureCapUpdate = new EventEmitter<string>();
  public fornitureCitiesUpdate = new EventEmitter<string>();
  public fornitureProvincesUpdate = new EventEmitter<string>();
  public fornitureAddressesUpdate = new EventEmitter<string>();

  // forniture autocomplete lists
  public fornitureCaps = [];
  public fornitureCities = [];
  public fornitureProvinces = [];
  public fornitureAddresses = [];

  public differentFornitureValid = true;
  public billShipMethodValid = true;

  public onDevelop = !environment.production;

  private fornitureLocationManualType = [];
  private residenceLocationManualType = [];

  //Controller checkbox Billship Method
  public isEmail: boolean;
  public isPosta: boolean;
  public checkboxClass: boolean;
  public checkboxClass2: boolean;
  public isMonthly: boolean;
  public isBimonthly: boolean;
  public checkboxClass3: boolean;
  public checkboxClass4: boolean;

  //Radio and Select Options
  public provinces = Addresses.getProvinces(true);
  public differentFornitures = [
    {
      label: "L'indirizzo di fornitura coincide con quello di residenza",
      value: false,
    },
    {
      label: "L'indirizzo di fornitura NON coincide con quello di residenza",
      value: true,
    },
  ];
  public billOptions = [
    {
      label: 'Via email',
      value: ShipMethod.email,
    },
    {
      label: 'Posta ordinaria',
      value: ShipMethod.posta,
    },
  ];

  public billPeriodicity = [
    {
      label: 'Mensile',
      value: BillingPeriodicity.monthly,
    },
    {
      label: 'Bimestrale',
      value: BillingPeriodicity.bimonthly,
    },
  ];

  private fornitureFields = {
    fornitureCap: new FormControl(this.onboarding.fornitureCap, {
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(5)],
    }),
    fornitureCity: new FormControl(this.onboarding.fornitureCity, {
      validators: [Validators.required],
    }),
    fornitureProvince: new FormControl(this.onboarding.fornitureProvince, {
      validators: [Validators.required],
    }),
    fornitureAddress: new FormControl(this.onboarding.fornitureAddress, {
      validators: [Validators.required],
    }),
    fornitureNumber: new FormControl(this.onboarding.fornitureNumber, {
      validators: [Validators.required, Validators.maxLength(9)],
    }),
  };

  private differentFornitureField = {
    differentForniture: new FormControl(this.onboarding.differentForniture, {
      validators: [Validators.required],
    }),
  };

  private defaultFields = {
    residenceCap: new FormControl(this.onboarding.residenceCap, {
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(5)],
    }),
    residenceCity: new FormControl(this.onboarding.residenceCity, {
      validators: [Validators.required],
    }),
    residenceProvince: new FormControl(this.onboarding.residenceProvince, {
      validators: [Validators.required /*, OnbValidators.province*/],
    }),
    residenceAddress: new FormControl(this.onboarding.residenceAddress, {
      validators: [Validators.required],
    }),
    residenceNumber: new FormControl(this.onboarding.residenceNumber, {
      validators: [Validators.required, Validators.maxLength(9)],
    }),
    billShipMethod: new FormControl(this.onboarding.billShipMethod, {
      validators: [Validators.required, Validators.requiredTrue],
    }),

    billingPeriodicity: new FormControl(this.onboarding.billingPeriodicity, {
      validators: [Validators.required, Validators.requiredTrue],
    }),
  };

  private getPostalCode(address) {
    return address.address_components.find((cmp) => cmp.types && cmp.types[0] === 'postal_code');
  }

  private getCity(address) {
    return address.address_components.find((cmp) => cmp.types[0] === 'administrative_area_level_3');
  }

  private getProvince(address) {
    return address.address_components.find((cmp) => cmp.types[0] === 'administrative_area_level_2');
  }

  private getAddress(address) {
    return address.address_components.find((cmp) => cmp.types[0] === 'route');
  }

  private getCivicNumber(address) {
    return address.address_components.find((cmp) => cmp.types[0] === 'street_number');
  }

  @Output() statusBack = new EventEmitter<string>();

  constructor(
    private api: ApiService,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private gtm: AlgGtmService,
    private modalDialogService: ModalDialogService,
    private viewContainer: ViewContainerRef,
    private utils: UtilsService,
    public translation: TranslationService
  ) {
    this.api.currentOnboarding
      .combineLatest(this.api.currentProduct)
      .debounceTime(100)
      .subscribe(([onboarding, product]) => {
        this.differentAddress = this.product.family && this.product.family.differentAddress;
        if (this.differentAddress) {
          this.onboarding.fornitureNation = 'IT';
        }

        this.setFornitureFields(onboarding.differentForniture || this.differentAddress, this.differentAddress);
        if (this.onboarding.billShipMethod === ShipMethod.email) {
          this.isEmail = true;
          this.isPosta = false;
        }
        if (this.onboarding.billShipMethod === ShipMethod.posta) {
          this.isPosta = true;
          this.isEmail = false;
        }

        if (this.onboarding.billShipMethod) {
          this.contractInfoForm.controls.billShipMethod.setValue(true);
        }

        if (this.onboarding.billingPeriodicity) {
          this.contractInfoForm.controls.billingPeriodicity.setValue(true);
        }
      });
  }

  ngOnInit() {
    this.contractInfoForm = new FormGroup({
      ...this.defaultFields,
      ...this.differentFornitureField,
    });
    this.contractInfoForm.statusChanges.subscribe((status) => {
      this.goAhead = this.contractInfoForm.status === 'VALID';
    });
    this.api.currentOnboarding.subscribe((onb) => {
      onb.manualInsertAddress = false;
      onb.manualInsertSupply = false;
      this.onboarding = onb;
      this.onboarding.residenceNation = 'IT';
    });
    this.api.currentProduct.subscribe((p) => {
      this.product = p;
      if (this.product && !!this.product.family && this.product.family.postaDisabled === true) {
        this.contractInfoForm.get('billShipMethod').clearValidators();
        this.contractInfoForm.get('billingPeriodicity').clearValidators();
      }

      if (this.product && this.product.source === 'Placet') {
        this.onboarding.billingPeriodicity = BillingPeriodicity.bimonthly;
      }
    });
    this.searchPipes();
    this.onFornitureChange();
  }

  public getAutocompleteOptions(types) {
    return {
      types,
      componentRestrictions: { country: 'IT' },
    };
  }

  public refreshResidenceAddress(event, type) {
    const address = this.getAddress(event);
    const civicNumber = this.getCivicNumber(event);

    if (civicNumber && civicNumber.long_name) {
      this.onboarding.residenceNumber = civicNumber.long_name.trim();
    }

    if (address && address.long_name) {
      this.contractInfoForm.controls.residenceAddress.setValue(address.long_name.trim());
    }

    if (this.residenceLocationManualType.indexOf('address') != -1 && address)
      this.residenceLocationManualType.splice(this.residenceLocationManualType.indexOf('address'), 1);

    if (type === 'address' && address && this.residenceLocationManualType.length === 0) {
      this.onboarding.manualInsertAddress = false;
      this.onboarding.manualInsertDateAddress = null;
      this.onboarding.manualInsertNameAddress = null;
    }
    this.refreshResidenceLocation(event, type);
  }

  public refreshResidenceLocation(event, type) {
    const postalCode = this.getPostalCode(event);
    const city = this.getCity(event);
    const province = this.getProvince(event);

    if (this.residenceLocationManualType.indexOf('postalCode') != -1 && postalCode)
      this.residenceLocationManualType.splice(this.residenceLocationManualType.indexOf('postalCode'), 1);
    if (this.residenceLocationManualType.indexOf('city') != -1 && city)
      this.residenceLocationManualType.splice(this.residenceLocationManualType.indexOf('city'), 1);
    if (this.residenceLocationManualType.indexOf('province') != -1 && province)
      this.residenceLocationManualType.splice(this.residenceLocationManualType.indexOf('province'), 1);

    if (this.residenceLocationManualType.length === 0) {
      this.onboarding.manualInsertAddress = false;
      this.onboarding.manualInsertDateAddress = null;
      this.onboarding.manualInsertNameAddress = null;
    }

    if (postalCode && postalCode.long_name) {
      this.contractInfoForm.controls.residenceCap.setValue(postalCode.long_name);
      (<HTMLInputElement>document.getElementById('input-residenceCap')).value = postalCode.long_name;
    } else {
      this.contractInfoForm.controls.residenceCap.setValue('');
    }

    if (city && city.long_name) {
      this.contractInfoForm.controls.residenceCity.setValue(city.long_name);
    } else {
      this.contractInfoForm.controls.residenceCity.setValue('');
    }

    if (province && province.long_name) {
      if (province.long_name.indexOf('Provincia di') > -1) {
        province.long_name = province.long_name.replace('Provincia di', '').trim();
      }

      if (province.long_name.indexOf('Città Metropolitana di') > -1) {
        province.long_name = province.long_name.replace('Città Metropolitana di', '').trim();
      }
      this.onboarding.residenceProvinceCode = province.short_name;
      this.contractInfoForm.controls.residenceProvince.setValue(province.long_name);
    } else {
      this.onboarding.residenceProvinceCode = '';
      this.contractInfoForm.controls.residenceProvince.setValue('');
    }
  }

  checkManualInsertOnProvince(type) {
    if (this.residenceLocationManualType.indexOf('province') != -1 && type === 'residence') {
      this.onboarding.residenceProvince = '';
      this.onboarding.residenceProvinceCode = '';
      this.contractInfoForm.controls.residenceProvince.setValue('');
    } else if (this.fornitureLocationManualType.indexOf('province') != -1 && type === 'supply') {
      this.onboarding.fornitureProvince = '';
      this.onboarding.fornitureProvinceCode = '';
      this.contractInfoForm.controls.fornitureCity.setValue('');
    }
  }

  checkManualInsertOnCity(type) {
    if (this.residenceLocationManualType.indexOf('city') != -1 && type === 'residence') {
      this.onboarding.residenceCity = '';
      this.contractInfoForm.controls.residenceCity.setValue('');
    } else if (this.fornitureLocationManualType.indexOf('city') != -1 && type === 'supply') {
      this.onboarding.fornitureCity = '';
      this.contractInfoForm.controls.fornitureCity.setValue('');
    }
  }

  public refreshFornitureAddress(event, type) {
    const address = this.getAddress(event);
    const civicNumber = this.getCivicNumber(event);

    if (civicNumber && civicNumber.long_name) {
      this.onboarding.fornitureNumber = civicNumber.long_name.trim();
    }

    if (address && address.long_name) {
      this.contractInfoForm.controls.fornitureAddress.setValue(address.long_name.trim());
    }

    if (this.fornitureLocationManualType.indexOf('address') != -1 && address)
      this.fornitureLocationManualType.splice(this.fornitureLocationManualType.indexOf('address'), 1);

    if (type === 'address' && address && this.fornitureLocationManualType.length === 0) {
      this.onboarding.manualInsertSupply = false;
      this.onboarding.manualInsertDateSupply = null;
      this.onboarding.manualInsertNameSupply = null;
    }

    this.refreshFornitureLocation(event, type);
  }

  public refreshFornitureLocation(event, type) {
    const postalCode = this.getPostalCode(event);
    const city = this.getCity(event);
    const province = this.getProvince(event);

    if (this.fornitureLocationManualType.indexOf('postalCode') != -1 && postalCode)
      this.fornitureLocationManualType.splice(this.fornitureLocationManualType.indexOf('postalCode'), 1);
    if (this.fornitureLocationManualType.indexOf('city') != -1 && city)
      this.fornitureLocationManualType.splice(this.fornitureLocationManualType.indexOf('city'), 1);
    if (this.fornitureLocationManualType.indexOf('province') != -1 && province)
      this.fornitureLocationManualType.splice(this.fornitureLocationManualType.indexOf('province'), 1);

    if (this.fornitureLocationManualType.length === 0) {
      this.onboarding.manualInsertSupply = false;
      this.onboarding.manualInsertDateSupply = null;
      this.onboarding.manualInsertNameSupply = null;
    }

    if (postalCode && postalCode.long_name) {
      this.contractInfoForm.controls.fornitureCap.setValue(postalCode.long_name);
      (<HTMLInputElement>document.getElementById('input-fornitureCap')).value = postalCode.long_name;
    } else {
      this.contractInfoForm.controls.fornitureCap.setValue('');
    }

    if (city && city.long_name) {
      this.contractInfoForm.controls.fornitureCity.setValue(city.long_name);
    } else {
      this.contractInfoForm.controls.fornitureCity.setValue('');
    }

    if (province && province.long_name) {
      if (province.long_name.indexOf('Provincia di') > -1) {
        province.long_name = province.long_name.replace('Provincia di', '').trim();
      }

      if (province.long_name.indexOf('Città Metropolitana di') > -1) {
        province.long_name = province.long_name.replace('Città Metropolitana di', '').trim();
      }

      this.onboarding.fornitureProvinceCode = province.short_name;
      this.contractInfoForm.controls.fornitureProvince.setValue(province.long_name);
    } else {
      this.onboarding.fornitureProvinceCode = '';
      this.contractInfoForm.controls.fornitureProvince.setValue('');
    }
  }

  public focusInGmapsResidenceLocation(type) {
    if (this.residenceLocationManualType.indexOf(type) === -1) this.residenceLocationManualType.push(type);
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    this.onboarding.manualInsertAddress = true;
    this.onboarding.manualInsertDateAddress = today;
    this.onboarding.manualInsertNameAddress = 'ONBOARDING WEB';
  }

  public focusInGmapsFornitureLocation(type) {
    if (this.fornitureLocationManualType.indexOf(type) === -1) this.fornitureLocationManualType.push(type);
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    this.onboarding.manualInsertSupply = true;
    this.onboarding.manualInsertDateSupply = today;
    this.onboarding.manualInsertNameSupply = 'ONBOARDING WEB';
  }

  private searchPipes() {
    this.residenceCapUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('cap', term))
      )
      .subscribe(
        (caps: any[]) => {
          this.cdr.markForCheck();
          this.residenceCaps = caps;
        },
        (err) => {
          this.residenceCaps = [];
          return [];
        }
      );

    this.residenceCitiesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('city', term))
      )
      .subscribe(
        (cities: any[]) => {
          this.cdr.markForCheck();
          this.residenceCities = cities;
        },
        (err) => {
          this.residenceCities = [];
          return [];
        }
      );

    this.residenceProvincesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('province', term))
      )
      .subscribe(
        (provinces: any[]) => {
          this.cdr.markForCheck();
          this.residenceProvinces = provinces;
        },
        (err) => {
          this.residenceProvinces = [];
          return [];
        }
      );

    this.residenceAddressesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getAddresses(term))
      )
      .subscribe(
        (addresses: any[]) => {
          this.cdr.markForCheck();
          this.residenceAddresses = addresses;
        },
        (err) => {
          this.residenceAddresses = [];
          return [];
        }
      );

    this.fornitureCapUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('cap', term))
      )
      .subscribe(
        (caps: any[]) => {
          this.cdr.markForCheck();
          this.fornitureCaps = caps;
        },
        (err) => {
          this.fornitureCaps = [];
          return [];
        }
      );

    this.fornitureCitiesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('city', term))
      )
      .subscribe(
        (cities: any[]) => {
          this.cdr.markForCheck();
          this.fornitureCities = cities;
        },
        (err) => {
          this.fornitureCities = [];
          return [];
        }
      );

    this.fornitureProvincesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getCities('province', term))
      )
      .subscribe(
        (provinces: any[]) => {
          this.cdr.markForCheck();
          this.fornitureProvinces = provinces;
        },
        (err) => {
          this.fornitureProvinces = [];
          return [];
        }
      );

    this.fornitureAddressesUpdate
      .pipe(
        distinctUntilChanged(),
        debounceTime(200),
        switchMap((term) => this.api.getAddresses(term))
      )
      .subscribe(
        (addresses: any[]) => {
          this.cdr.markForCheck();
          this.fornitureAddresses = addresses;
        },
        (err) => {
          this.fornitureAddresses = [];
          return [];
        }
      );
  }

  public emptyFieldsForniture() {
    this.onboarding.fornitureProvince = '';
    this.onboarding.fornitureCap = '';
    this.onboarding.fornitureAddress = '';
    this.onboarding.fornitureCity = '';
    this.onboarding.fornitureNumber = '';
  }

  private setFornitureFields(enable: boolean, noChoice: boolean) {
    let fields = { ...this.defaultFields };
    if (enable) {
      fields = Object.assign(fields, { ...this.fornitureFields });
      if (!noChoice) {
        fields = Object.assign(fields, { ...this.differentFornitureField });
      }
    } else {
      fields = Object.assign(fields, { ...this.differentFornitureField });
      this.contractInfoForm.removeControl('differentForniture');
      this.emptyFieldsForniture();
    }
    this.contractInfoForm = new FormGroup(fields);
    this.contractInfoForm.statusChanges.subscribe((status) => {
      this.goAhead = this.contractInfoForm.status === 'VALID' && getStepNumber(Status.contractInfo) <= getStepNumber(this.onboarding.webStatus);
    });
  }

  public onFornitureChange() {
    this.setFornitureFields(this.onboarding.differentForniture, false);
    this.enableOtherAddress = this.onboarding.differentForniture;
    this.onboarding.fornitureNation = this.onboarding.differentForniture ? 'IT' : null;
  }

  nextStep() {
    this.markFormGroupTouched(this.contractInfoForm);
    const invalidControl = this.el.nativeElement.querySelector('form .ng-invalid');
    if (invalidControl) return this.scrollOnInvalidElement(invalidControl);
    if (this.contractInfoForm.status === 'VALID') {
      // if Email is the only method
      if (this.product && !!this.product.family && this.product.family.postaDisabled === true) {
        this.onboarding.billShipMethod = ShipMethod.email;
      }
      this.statusBack.emit('current');
      if (this.product && this.product.source === 'Placet') {
        this.onboarding.billingPeriodicity = BillingPeriodicity.bimonthly;
      }
      this.api.nextStep(this.onboarding, Status.contractInfo);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    (<any>Object).values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control.controls) this.markFormGroupTouched(control);
    });
  }

  private scrollOnInvalidElement(invalidControl) {
    this.controlCheckbox();
    window.scroll({
      top: this.getTopOffset(invalidControl),
      left: 0,
      behavior: 'smooth',
    });

    fromEvent(window, 'scroll')
      .pipe(debounceTime(100), take(1))
      .subscribe(() => {
        invalidControl.firstElementChild.focus();
      });
  }

  private getTopOffset(controlEl: HTMLElement): number {
    const labelOffset = 50;
    return controlEl.getBoundingClientRect().top + window.scrollY - labelOffset;
  }

  changeBillShip(value, event) {
    if (value === 'email') {
      if (event.target.checked) {
        this.isEmail = true;
        this.onboarding.billShipMethod = ShipMethod.email;
      } else {
        this.isEmail = false;
        this.isPosta = false;
        this.onboarding.billShipMethod = null;
      }
      this.checkboxClass = !this.isEmail;
      this.checkboxClass2 = this.checkboxClass;
    } else if (value === 'posta') {
      if (event.target.checked) {
        this.isPosta = true;
        this.onboarding.billShipMethod = ShipMethod.posta;
      } else {
        this.isEmail = false;
        this.isPosta = false;
        this.onboarding.billShipMethod = null;
      }
      this.checkboxClass2 = !this.isPosta;
      this.checkboxClass = this.checkboxClass2;
    }
  }

  changeBillingPeriodicity(value, event) {
    if (value === 'monthly') {
      if (event.target.checked) {
        this.isMonthly = true;
        this.onboarding.billingPeriodicity = BillingPeriodicity.monthly;
      } else {
        this.isMonthly = false;
        this.isBimonthly = false;
        this.onboarding.billingPeriodicity = null;
      }
      this.checkboxClass3 = !this.isMonthly;
      this.checkboxClass4 = this.checkboxClass3;
    } else if (value === 'bimonthly') {
      if (event.target.checked) {
        this.isBimonthly = true;
        this.onboarding.billingPeriodicity = BillingPeriodicity.bimonthly;
      } else {
        this.isMonthly = false;
        this.isBimonthly = false;
        this.onboarding.billingPeriodicity = null;
      }
      this.checkboxClass4 = !this.isBimonthly;
      this.checkboxClass3 = this.checkboxClass4;
    }
  }

  changeStatus(value) {
    this.gtm.submitOnbEvent('clic su indietro', 'contract_info');
    this.statusBack.emit(value);
  }

  controlCheckbox() {
    if (this.isEmail && !this.isPosta) {
      return (this.checkboxClass = true);
    } else if (!this.isEmail && this.isPosta) {
      return (this.checkboxClass2 = true);
    } else {
      this.checkboxClass = true;
      this.checkboxClass2 = true;
    }

    if (this.isMonthly && !this.isBimonthly) {
      return (this.checkboxClass3 = true);
    } else if (!this.isMonthly && this.isBimonthly) {
      return (this.checkboxClass4 = true);
    } else {
      this.checkboxClass3 = true;
      this.checkboxClass4 = true;
    }
  }

  openModal(type) {
    let modalContent;
    if (type === 'billing-spedition') {
      modalContent = {
        title: this.translation.translate('BILL_SPEDITION_MODAL_TITLE'),
        description: this.translation.translate(this.product.source === 'Placet' ? 'BILL_SPEDITION_MODAL_CONTENT_PLACET' : 'BILL_SPEDITION_MODAL_CONTENT'),
      };
    }
    this.modal(modalContent || {});
  }

  private modal(modalContent) {
    this.utils.disableOverflow();
    this.modalDialogService.openDialog(this.viewContainer, {
      title: modalContent.title,
      childComponent: AlgConditionModalComponent,
      settings: {
        closeButtonClass: 'close theme-icon-close',
        headerTitleClass: ' modal-title modal-title-underline',
      },
      data: {
        description: modalContent.description,
      },
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/components/steps/alg-contract-info/alg-contract-info.component.ts